// src/auth/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getAuth, onIdTokenChanged, signOut as firebaseSignOut } from "firebase/auth";
import { clearTvSessionBusinessContext } from "../services/firestore/tvBusinessContext";
import {
  ACCOUNT_TYPES,
  getOrCreateUserProfile,
  setUserAccountType,
} from "../services/profileService";
import useStore from "../store/useStore";

const Ctx = createContext(null);
const CREATOR_LIKE_ROLES = new Set(["creator", "individual"]);

function buildUser(firebaseUser, claims) {
  if (!firebaseUser) return null;
  return {
    id: firebaseUser.uid,
    uid: firebaseUser.uid,
    name: firebaseUser.displayName || firebaseUser.email || "User",
    displayName: firebaseUser.displayName || null,
    email: firebaseUser.email || null,
    photoURL: firebaseUser.photoURL || null,
    claims: claims || {},
  };
}

function getRolesFromClaims(claims) {
  if (!claims) return [];
  if (Array.isArray(claims.roles)) return claims.roles.filter(Boolean);
  if (typeof claims.role === "string" && claims.role) return [claims.role];
  return [];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const setUserProfile = useStore((state) => state.setUserProfile);
  const clearUserProfile = useStore((state) => state.clearUserProfile);

  useEffect(() => {
    const auth = getAuth();
    const syncUserProfile = async (firebaseUser, tokenClaims = {}) => {
      try {
        let profile = await getOrCreateUserProfile(firebaseUser);
        const isAdminByClaim = Array.isArray(tokenClaims?.roles)
          ? tokenClaims.roles.includes("admin")
          : tokenClaims?.role === "admin" || tokenClaims?.admin === true;

        if (
          isAdminByClaim &&
          String(profile?.accountType || "").toLowerCase() !== ACCOUNT_TYPES.BUSINESS
        ) {
          const patch = await setUserAccountType(firebaseUser.uid, ACCOUNT_TYPES.BUSINESS);
          profile = {
            ...(profile || {}),
            uid: firebaseUser.uid,
            ...patch,
          };
        }

        if (profile) {
          setUserProfile(profile);
        }
      } catch (profileErr) {
        console.error("[Auth] Failed to sync user profile:", profileErr);
      }
    };

    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      setLoading(true);

      if (!firebaseUser) {
        clearTvSessionBusinessContext();
        setUser(null);
        setClaims(null);
        clearUserProfile();
        setLoading(false);
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        setClaims(tokenResult.claims || {});
        await syncUserProfile(firebaseUser, tokenResult.claims || {});
        setUser(buildUser(firebaseUser, tokenResult.claims || {}));
      } catch (err) {
        console.error("[Auth] Failed to read token claims:", err);
        setClaims({});
        await syncUserProfile(firebaseUser, {});
        setUser(buildUser(firebaseUser, {}));
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [clearUserProfile, setUserProfile]);

  const roles = useMemo(() => getRolesFromClaims(claims), [claims]);
  const isAdmin = roles.includes("admin");
  const role = isAdmin ? "admin" : roles[0] || "creator";
  const isCreator = isAdmin || CREATOR_LIKE_ROLES.has(role);
  const creatorId = user?.uid || null;

  const signOut = async () => {
    const auth = getAuth();
    try {
      await firebaseSignOut(auth);
      clearTvSessionBusinessContext();
    } catch (err) {
      console.error("[Auth] Sign out failed:", err);
    }
  };

  const value = useMemo(
    () => ({
      user,
      claims,
      roles,
      role,
      isAdmin,
      isCreator,
      creatorId,
      loading,
      signOut,
    }),
    [user, claims, roles, role, isAdmin, isCreator, creatorId, loading]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
